import { Controller } from "stimulus";

const UPLOAD_ERROR = "Could not upload image";

export default class extends Controller {
  static targets = ["fileInput", "emptyState", "filledState", "preview", "urlInput", "imageWrapper", "loading", "error"];

  connect() {
    this.restoreSaved();
  }

  disconnect() {
    this.cancelUpload();
  }

  restoreSaved() {
    const url = this.urlInputTarget.value;
    if (url) {
      this.showPreview(url);
    } else {
      this.showEmpty();
    }
  }

  showEmpty() {
    this.previewTarget.removeAttribute('src');
    this.emptyStateTarget.classList.remove('hidden');
    this.filledStateTarget.classList.add('hidden');
  }

  showPreview(url) {
    this.previewTarget.src = url;
    this.filledStateTarget.classList.remove('hidden');
    this.emptyStateTarget.classList.add('hidden');
  }

  triggerFileInput() {
    this.fileInputTarget.click();
  }

  handleFileChange(event) {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';

    this.cancelUpload();
    this.hideError();

    this.objectUrl = URL.createObjectURL(file);
    this.showPreview(this.objectUrl);
    this.setLoading(true);

    const controller = new AbortController();
    this.abortController = controller;

    const formData = new FormData();
    formData.append('file', file);

    fetch('/dashboard/shared/image_uploads', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json',
      },
      body: formData,
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.url) throw new Error(data?.error || UPLOAD_ERROR);
        return data;
      })
      .then((data) => {
        if (this.abortController !== controller) return;
        this.abortController = null;
        this.setUrl(data.url);
        this.swapInUploadedImage(data.url);
      })
      .catch((error) => {
        if (controller.signal.aborted || this.abortController !== controller) return;
        this.abortController = null;
        this.endLoading();
        this.restoreSaved();
        this.showError(error.message || UPLOAD_ERROR);
      });
  }

  // Keep the dimmed local preview up until the uploaded image has actually loaded,
  // so the swap doesn't flash an empty frame.
  swapInUploadedImage(url) {
    const image = new Image();
    this.pendingImage = image;
    image.onload = () => {
      if (this.pendingImage !== image) return;
      this.pendingImage = null;
      this.previewTarget.src = url;
      this.endLoading();
    };
    // The upload succeeded, so keep the local preview rather than showing a broken image.
    image.onerror = () => {
      if (this.pendingImage !== image) return;
      this.pendingImage = null;
      this.setLoading(false);
    };
    image.src = url;
  }

  setLoading(loading) {
    this.previewTarget.classList.toggle('opacity-50', loading);
    this.imageWrapperTarget.classList.toggle('min-h-20', loading);
    this.loadingTarget.classList.toggle('hidden', !loading);
  }

  endLoading() {
    this.setLoading(false);
    this.revokeObjectUrl();
  }

  cancelUpload() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.pendingImage = null;
    this.endLoading();
  }

  revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove('hidden');
  }

  hideError() {
    this.errorTarget.textContent = '';
    this.errorTarget.classList.add('hidden');
  }

  setUrl(url) {
    this.urlInputTarget.value = url;
    this.urlInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.urlInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  removeImage() {
    this.cancelUpload();
    this.hideError();
    this.urlInputTarget.value = '';
    this.showEmpty();
    this.urlInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.urlInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
